import { TransactionRuleGroup } from "@common/types/transactionRule";
import { useMutation } from "@tanstack/react-query";
import { TransactionRuleGroupDto } from "@common/dto/transactionRule/transaction-rule-group.dto";
import { Counterparty } from "@common/types/counterparty";
import { ServerInferRequest } from "@ts-rest/core";
import { ruleContract } from "@common/contracts/rule.contract";
import { queryClient } from "../queryClient";
import { useWorkspaceContext } from "../state/workspaceContext";
import { getRuleGroupsQueryKey, rulesContractClient } from "./rules.api";

export interface CreateRuleGroupParams {
    workspaceId: string;
    ruleGroup: ServerInferRequest<typeof ruleContract.createRuleGroup>["body"];
}

export const useCreateRuleGroup = (onSuccess?: () => void) =>
    useMutation({
        mutationFn: async ({
            workspaceId,
            ruleGroup,
        }: CreateRuleGroupParams) => {
            const response = await rulesContractClient.createRuleGroup({
                params: {
                    workspaceId,
                },
                body: ruleGroup,
            });

            return TransactionRuleGroupDto.deserialize(response.body.rule);
        },
        onSuccess: (data, variables) => {
            // Update the query cache with the new rule group
            const queryKey = getRuleGroupsQueryKey(variables.workspaceId);
            const currentData = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
                counterparties: Counterparty[];
                transactionCounts: Record<string, number>;
            }>(queryKey);

            if (currentData) {
                queryClient.setQueryData(queryKey, {
                    ...currentData,
                    ruleGroups: [...currentData.ruleGroups, data],
                });
            }
            onSuccess?.();
        },
    });

export interface UpdateRuleGroupParams {
    workspaceId: string;
    groupId: string;
    ruleGroup: ServerInferRequest<typeof ruleContract.updateRuleGroup>["body"];
}

export const useUpdateRuleGroup = (onSuccess?: () => void) => {
    const { activeWorkspaceId } = useWorkspaceContext();
    const queryKey = getRuleGroupsQueryKey(activeWorkspaceId ?? "");

    return useMutation({
        mutationFn: async ({
            workspaceId,
            groupId,
            ruleGroup,
        }: UpdateRuleGroupParams) => {
            const response = await rulesContractClient.updateRuleGroup({
                params: {
                    workspaceId,
                    groupId,
                },
                body: ruleGroup,
            });

            return TransactionRuleGroupDto.deserialize(response.body.rule);
        },
        onMutate: async ({ groupId, ruleGroup }: UpdateRuleGroupParams) => {
            await queryClient.cancelQueries({ queryKey });

            const previousRuleGroups = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
            }>(queryKey);

            const updatedRuleGroups = previousRuleGroups?.ruleGroups
                .map((g) => (g.id === groupId ? { ...g, ...ruleGroup } : g))
                .toSorted((a, b) => a.order - b.order);

            queryClient.setQueryData(queryKey, {
                ...previousRuleGroups,
                ruleGroups: updatedRuleGroups,
            });

            return { previousRuleGroups };
        },
        onError: (_, __, context) => {
            // we need to find the previous rule's position and move it back
            queryClient.setQueryData(
                queryKey,
                context?.previousRuleGroups ?? [],
            );
        },
        onSuccess: () => {
            onSuccess?.();
        },
    });
};

export type RuleGroupRemovalError = Error;

export interface RuleGroupRemovalOptions {
    onSuccess?: () => void;
    onError?: (error: RuleGroupRemovalError) => void;
}

export function useRuleGroupRemovalMutation(
    ruleGroup: TransactionRuleGroup,
    { onSuccess, onError }: RuleGroupRemovalOptions = {},
) {
    return useMutation({
        mutationFn: () =>
            rulesContractClient.deleteRuleGroup({
                params: {
                    workspaceId: ruleGroup.workspaceId,
                    ruleId: ruleGroup.id,
                },
            }),

        onSuccess: () => {
            const queryKey = getRuleGroupsQueryKey(ruleGroup.workspaceId);
            const currentData = queryClient.getQueryData<{
                ruleGroups: TransactionRuleGroup[];
            }>(queryKey);

            if (currentData) {
                const updatedRuleGroups = currentData.ruleGroups.filter(
                    (group) => group.id !== ruleGroup.id,
                );

                queryClient.setQueryData(queryKey, {
                    ...currentData,
                    ruleGroups: updatedRuleGroups,
                });
            }
            onSuccess?.();
        },
        onError: (error) => {
            if (onError) {
                onError(error as RuleGroupRemovalError);
            }
        },
    });
}
